import { Button, Checkbox, Form, Input, Toast } from 'antd-mobile-v5'
import { EyeInvisibleOutline, EyeOutline } from 'antd-mobile-icons'
import { WingBlank } from 'antd-mobile'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useHistory, useLocation } from 'react-router-dom'
import React, { useRef, useState } from 'react'

import { getParameterByName, isWechatBrowser, UClick } from '../../components/common/utils'
import { setPadiWxId } from '../../components/common/cookie'
import { useEmailLogin, useLanguage, useSaveProfile } from '../../services/hooks/user'
import SelectLanguage from '../../components/Login/SelectLanguage'
import MobileVerify from '../Register/MobileVerify'
import { useGetM2PhoneNumber, useMainlandIp } from '../../services/hooks'
import { useShowMessage } from '../../services/hooks/ui'
import { isTaiwanUser } from '../../utils/user'

import './emailLogin.css'
import styles from '../Register/Register.module.css'

function PasswordInput({ value, onChange, placeholder, showPassword, onToggle }) {
  const inputRef = useRef(null)

  const handleToggle = () => {
    const nativeInput = inputRef.current?.nativeElement
    const pos = nativeInput?.selectionEnd ?? value?.length ?? 0
    onToggle()
    requestAnimationFrame(() => {
      const el = inputRef.current?.nativeElement
      if (!el) return
      el.focus()
      try {
        el.setSelectionRange(pos, pos)
      } catch (e) {
        // password type 不支持 setSelectionRange，忽略
      }
    })
  }

  return (
    <div className="password-input-wrapper">
      <Input
        ref={inputRef}
        type={showPassword ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
      <span className="password-eye-icon" onMouseDown={(e) => e.preventDefault()} onClick={handleToggle}>
        {showPassword ? <EyeOutline /> : <EyeInvisibleOutline />}
      </span>
    </div>
  )
}

function EmailLoginPage() {
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const lang = useLanguage()
  const { push } = useHistory()
  const [account, setAccount] = useState(null)
  const [showMobileVerify, setMobileVerifyStatus] = useState(false)
  const [countryCode, setCountryCode] = useState('86')
  const [mobileVerified, setMobileVerified] = useState(false)
  const [agreement1, setAgreement1] = useState(false)
  const [agreement2, setAgreement2] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const showMessage = useShowMessage()
  const referer = getParameterByName('referer') || ''
  const { data: isMainlandIp } = useMainlandIp({})
  const { search } = useLocation()
  const { run: getM2PhoneNumber, data } = useGetM2PhoneNumber({
    onSuccess: (data) => {
      if (data) {
        if (data.phoneNumber) {
          setCountryCode(data.countryCode || '86')
          form.setFieldsValue({ mobile: data.phoneNumber })
        }
        setMobileVerifyStatus(true)
      }
    },
  })
  const { loading, run: login } = useEmailLogin({
    onSuccess: (data) => {
      if (data.userMobiles && data.userMobiles.length > 0) {
        push(`/mobile_selection${search}`, { mobileList: data.userMobiles, padiToken: data.token })
        return
      }
      // 只有「确在微信内置浏览器」才必须保留 28 位 openid（富友支付要用它拿 openid），
      // 其余环境一律写入本次登录的新 token：用 isWechat() 会把「cookie 恰好 28 位」也算作微信环境，
      // 桌面端残留旧 openid 时新 token 被静默丢弃 → 列表请求继续带旧 token、永远「登录过期」
      if (!isWechatBrowser()) setPadiWxId(data.token)
      const nextPage = getParameterByName('referer') || '/certs'
      if (nextPage.indexOf('http') === 0) {
        if (
          nextPage.indexOf('club-api-staging.padi') >= 0 ||
          nextPage.indexOf('club-api.padi') >= 0 ||
          nextPage.indexOf('china-wechat-api-stage.padi.com.cn/club-api') >= 0 ||
          nextPage.indexOf('china-wechat-api.padi.com.cn/club-api') >= 0
        ) {
          let refererUrl
          if (nextPage.indexOf('?') > 0) {
            refererUrl = nextPage + '&padiToken=' + data.token
          } else {
            refererUrl = nextPage + '?padiToken=' + data.token
          }
          window.location.href = refererUrl
          return
        }
        window.location.href = nextPage
      } else {
        if (getParameterByName('src') === 'preview') {
          UClick('buyCardsPreviewLoginSuccess')
        }
        push(nextPage)
      }
    },
    onError: (error, params) => {
      if (error.code === 'mobile.not.verified') {
        setAccount(params[0])
        getM2PhoneNumber(params[0].username)
      }
    },
  })

  const { loading: profileSaving, run: saveProfile } = useSaveProfile({
    onSuccess: () => {
      handleLogin(account)
    },
    onError: () => {
      showMessage('"common-errorStatus')
    },
  })

  const onMobileVerified = ({ verified }) => {
    setMobileVerified(verified)
  }

  const loginAfterVerifyMobile = (values) => {
    const { mobile } = values
    const email = form.getFieldValue('username')
    if (isMainlandIp && countryCode === '86' && !mobileVerified) {
      return showMessage('register-mobile-not-verified')
    }
    let saveData = {
      email,
      countryCode,
      mobile,
    }
    saveProfile(saveData)
  }

  const verifyAgreements = () => {
    if (isMainlandIp) {
      return agreement1 && agreement2
    } else {
      return !!agreement1
    }
  }

  const handleLogin = (values) => {
    let data = values
    if (!verifyAgreements()) {
      Toast.show({
        content: '请先阅读并勾选协议。',
      })
      return
    }
    if (isMainlandIp) {
      data.userAgreementTypes = ['USER_AGREEMENT', 'CROSS_BORDER_DATA_TRANSFER', 'GLOBAL_AGREEMENT']
    } else {
      data.userAgreementTypes = ['GLOBAL_AGREEMENT']
    }
    login(data)
  }

  return (
    <div className="home-page">
      <SelectLanguage />
      <div className="logo">
        <img src={require('../../assets/images/common/PADI-logo.png')} alt="logo" />
      </div>
      {!showMobileVerify ? (
        <>
          <Form
            form={form}
            onFinish={(values) => handleLogin(values)}
            validateTrigger="submit"
            footer={
              <div>
                <div className="forget-password" style={{ textAlign: 'end', paddingBottom: '6px' }}>
                  <Link
                    to={referer ? `/register?referer=${referer}` : '/register'}
                    onClick={() => referer.indexOf('shared-logs') >= 0 && UClick('emailLogin-sign-up-click')}
                  >
                    <FormattedMessage id="loginComponent-signUp" />
                  </Link>
                  <span style={{ color: '#1890ff' }}>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                  <span onClick={() => push('/find_account/user_info')} style={{ color: '#1890ff' }}>
                    <FormattedMessage id="findme-header" />
                  </span>
                  <span style={{ color: '#1890ff' }}>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                  <span onClick={() => push('/reset_pwd')} style={{ color: '#1890ff' }}>
                    <FormattedMessage id="loginComponent-forgotPassword" />
                  </span>
                </div>
                <Button block type="submit" color="primary" loading={loading}>
                  {formatMessage({ id: 'emailLogin-login' })}
                </Button>
              </div>
            }
          >
            <Form.Header>
              <FormattedMessage id="loginComponent-footer" />
            </Form.Header>
            <Form.Item
              label={formatMessage({ id: 'common-email' })}
              name="username"
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: formatMessage({ id: 'form-email-empty-error' }),
                },
                {
                  type: 'email',
                  message: formatMessage({ id: 'form-email-format-error' }),
                },
              ]}
            >
              <Input placeholder={formatMessage({ id: 'emailLogin-emailInput' })} />
            </Form.Item>
            <Form.Item
              label={formatMessage({ id: 'loginComponent-pwd' })}
              name="password"
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: formatMessage({ id: 'common-reset' }),
                },
              ]}
            >
              <PasswordInput
                placeholder={formatMessage({ id: 'common-passwordInput' })}
                showPassword={showPassword}
                onToggle={() => setShowPassword((v) => !v)}
              />
            </Form.Item>
          </Form>
          <WingBlank>
            <Checkbox className={styles.agreement} checked={agreement1} onChange={(val) => setAgreement1(val)}>
              {isMainlandIp ? (
                <FormattedMessage
                  id="common-agreement-hint-mainland"
                  values={{
                    link1: (
                      <a
                        rel="noopener noreferrer"
                        target="_blank"
                        href="https://assets.padi.com.cn/agreements/club-user-agreement.html"
                      >
                        <FormattedMessage id="common-club-agreement" />
                      </a>
                    ),
                    link2: (
                      <a
                        rel="noopener noreferrer"
                        target="_blank"
                        href={
                          isTaiwanUser()
                            ? 'https://www.padi.com.tw/zh-hant/privacy'
                            : `https://assets.padi.com.cn/agreement_${lang}.html`
                        }
                      >
                        <FormattedMessage id="common-agreement" />
                      </a>
                    ),
                  }}
                />
              ) : (
                <FormattedMessage
                  id="common-agreement-hint"
                  values={{
                    link: (
                      <a
                        rel="noopener noreferrer"
                        target="_blank"
                        href={
                          isTaiwanUser()
                            ? 'https://www.padi.com.tw/zh-hant/privacy'
                            : `https://assets.padi.com.cn/agreement_${lang}.html`
                        }
                      >
                        <FormattedMessage id="common-agreement" />
                      </a>
                    ),
                  }}
                />
              )}
            </Checkbox>
            {isMainlandIp && (
              <Checkbox className={styles.agreement} checked={agreement2} onChange={(val) => setAgreement2(val)}>
                <FormattedMessage
                  id="common-info-agreement-hint"
                  values={{
                    link: (
                      <a
                        rel="noopener noreferrer"
                        target="_blank"
                        href={`https://assets.padi.com.cn/personalInfo_agreement_${lang}.html`}
                      >
                        <FormattedMessage id="common-info-agreement" />
                      </a>
                    ),
                  }}
                />
              </Checkbox>
            )}
          </WingBlank>
        </>
      ) : (
        <Form
          layout="horizontal"
          form={form}
          onFinish={loginAfterVerifyMobile}
          onFinishFailed={() => {}}
          onValuesChange={() => {}}
          validateTrigger="submit"
          footer={
            <Button block type="submit" color="primary" loading={loading || profileSaving}>
              {formatMessage({ id: 'common-comfirmBtn' })}
            </Button>
          }
        >
          <Form.Header>
            <FormattedMessage id="setMobile-title" />
          </Form.Header>
          <MobileVerify
            form={form}
            emailVerified={true}
            onVerifyResult={onMobileVerified}
            setCountryCode={setCountryCode}
            countryCode={countryCode}
            isMainlandIp={isMainlandIp}
            email={form.getFieldValue('username')}
          />
        </Form>
      )}
    </div>
  )
}

export default EmailLoginPage
